import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { apiGet, apiPatch, apiPost, SUPER_TOKEN_KEY } from '../../api/client';
import { tokens } from '../../design-system/tokens';

export type LandingDemoMeeting = {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string | null;
  message: string | null;
  scheduledAt: string;
  meetingLabel: string;
  meetUrl: string | null;
  status: string;
  emailSent: boolean;
  confirmationEmailSent: boolean;
  internalNotes: string | null;
  clientOutcome: string | null;
  reminderEmailSentAt: string | null;
  reminderEmailOpenedAt: string | null;
  reminderWhatsappSentAt: string | null;
  reminderWhatsappError: string | null;
  createdAt: string;
  whatsappRemindersConfigured?: boolean;
};

type MeetingsResponse = {
  year: number;
  month: number;
  meetings: LandingDemoMeeting[];
  whatsappRemindersConfigured: boolean;
};

const DAY_HEADERS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const CAL_TZ = 'America/Montevideo';

const OUTCOME_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Pendiente' },
  { value: 'interested', label: 'Mostró interés' },
  { value: 'not_interested', label: 'Sin interés' },
  { value: 'no_show', label: 'No se presentó' },
];

function getMonthGrid(year: number, month: number): { date: Date; isCurrentMonth: boolean }[][] {
  const first = new Date(year, month - 1, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const gridStart = new Date(year, month - 1, 1 - startOffset);
  const weeks: { date: Date; isCurrentMonth: boolean }[][] = [];
  const cursor = new Date(gridStart);
  for (let w = 0; w < 6; w++) {
    const week: { date: Date; isCurrentMonth: boolean }[] = [];
    for (let d = 0; d < 7; d++) {
      week.push({
        date: new Date(cursor),
        isCurrentMonth: cursor.getMonth() === month - 1,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Clave YYYY-MM-DD del día mostrado en el grid (fecha local del calendario). */
function calendarDayKey(d: Date): string {
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

/** Clave YYYY-MM-DD de una reunión en zona Uruguay. */
function meetingDayKey(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CAL_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

function formatTimeUy(iso: string): string {
  return new Intl.DateTimeFormat('es-UY', {
    timeZone: CAL_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

function isCancelled(m: LandingDemoMeeting): boolean {
  return m.status === 'cancelled';
}

function emailBadge(m: LandingDemoMeeting): { label: string; color: string } {
  if (m.reminderEmailOpenedAt) return { label: 'Mail abierto', color: '#15803d' };
  if (m.reminderEmailSentAt) return { label: 'Mail enviado', color: '#a16207' };
  return { label: 'Sin recordatorio mail', color: '#64748b' };
}

function waBadge(m: LandingDemoMeeting): { label: string; color: string } {
  if (m.reminderWhatsappSentAt) return { label: 'WA enviado', color: '#15803d' };
  if (m.reminderWhatsappError) return { label: 'WA error', color: '#b91c1c' };
  return { label: 'Sin recordatorio WA', color: '#64748b' };
}

export function SuperDemoMeetingsPage() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem(SUPER_TOKEN_KEY) : null;
  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [meetings, setMeetings] = useState<LandingDemoMeeting[]>([]);
  const [waConfigured, setWaConfigured] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(today);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [outcomeDraft, setOutcomeDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiGet<MeetingsResponse>(
        `/api/super/landing-demo-meetings?year=${year}&month=${month}`,
        token
      );
      setMeetings(data.meetings);
      setWaConfigured(Boolean(data.whatsappRemindersConfigured));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar las reuniones');
    } finally {
      setLoading(false);
    }
  }, [token, year, month]);

  useEffect(() => {
    void load();
  }, [load]);

  const meetingsByDay = useMemo(() => {
    const map = new Map<string, LandingDemoMeeting[]>();
    for (const m of meetings) {
      const key = meetingDayKey(m.scheduledAt);
      const list = map.get(key) ?? [];
      list.push(m);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
    }
    return map;
  }, [meetings]);

  const selectedMeetings = useMemo(() => {
    if (!selectedDate) return [];
    return meetingsByDay.get(calendarDayKey(selectedDate)) ?? [];
  }, [selectedDate, meetingsByDay]);

  const selected = useMemo(
    () => meetings.find((m) => m.id === selectedId) ?? null,
    [meetings, selectedId]
  );

  useEffect(() => {
    if (!selected) {
      setNotesDraft('');
      setOutcomeDraft('');
      setActionMsg('');
      return;
    }
    setNotesDraft(selected.internalNotes ?? '');
    setOutcomeDraft(selected.clientOutcome ?? '');
    setActionMsg('');
  }, [selected]);

  useEffect(() => {
    if (!selectedId) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setSelectedId(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId]);

  const monthLabel = new Intl.DateTimeFormat('es-UY', { month: 'long', year: 'numeric' }).format(
    new Date(year, month - 1, 1)
  );

  function navigateMonth(delta: number) {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
    setSelectedDate(new Date(d.getFullYear(), d.getMonth(), 1));
    setSelectedId(null);
  }

  async function saveDetail() {
    if (!token || !selected) return;
    setSaving(true);
    setActionMsg('');
    try {
      const updated = await apiPatch<LandingDemoMeeting>(
        `/api/super/landing-demo-meetings/${selected.id}`,
        {
          internalNotes: notesDraft.trim() || null,
          clientOutcome: outcomeDraft || null,
        },
        token,
        { successMessage: 'Guardado' }
      );
      setMeetings((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
      setActionMsg('Detalle guardado.');
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  }

  async function sendReminder(channels: Array<'email' | 'whatsapp'>, force = false) {
    if (!token || !selected) return;
    setReminding(true);
    setActionMsg('');
    try {
      const data = await apiPost<{
        result: {
          email: { sent: boolean; skipped?: string; error?: string };
          whatsapp: { sent: boolean; skipped?: string; error?: string };
        };
        meeting: LandingDemoMeeting | null;
      }>(
        `/api/super/landing-demo-meetings/${selected.id}/send-reminder`,
        { channels, force },
        token,
        { successMessage: 'Recordatorio disparado' }
      );
      if (data.meeting) {
        setMeetings((prev) =>
          prev.map((m) => (m.id === data.meeting!.id ? { ...m, ...data.meeting! } : m))
        );
      }
      const bits: string[] = [];
      if (channels.includes('email')) {
        bits.push(
          data.result.email.sent
            ? 'Email enviado'
            : data.result.email.error
              ? `Email: ${data.result.email.error}`
              : `Email: ${data.result.email.skipped ?? 'omitido'}`
        );
      }
      if (channels.includes('whatsapp')) {
        bits.push(
          data.result.whatsapp.sent
            ? 'WhatsApp enviado'
            : data.result.whatsapp.error
              ? `WhatsApp: ${data.result.whatsapp.error}`
              : `WhatsApp: ${data.result.whatsapp.skipped ?? 'omitido'}`
        );
      }
      setActionMsg(bits.join(' · '));
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : 'No se pudo enviar');
    } finally {
      setReminding(false);
    }
  }

  if (!token) {
    return <Navigate to="/super/login" replace />;
  }

  const weeks = getMonthGrid(year, month);
  const selectedCancelled = selected ? isCancelled(selected) : false;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '28px 24px 48px' }}>
      <header style={{ marginBottom: tokens.spacing[6] }}>
        <h1 style={{ margin: '0 0 8px', fontSize: 28, fontWeight: 700, color: tokens.colors.neutral[900] }}>
          Demos agendadas
        </h1>
        <p style={{ margin: 0, color: tokens.colors.neutral[600], fontSize: 15 }}>
          Detalle, outcome y recordatorios (email + WhatsApp template). Incluye demos canceladas.
        </p>
      </header>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: tokens.spacing[4],
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button type="button" onClick={() => navigateMonth(-1)} style={navBtnStyle}>
            ←
          </button>
          <span style={{ fontWeight: 600, textTransform: 'capitalize', minWidth: 180, textAlign: 'center' }}>
            {monthLabel}
          </span>
          <button type="button" onClick={() => navigateMonth(1)} style={navBtnStyle}>
            →
          </button>
        </div>
        <button type="button" onClick={() => void load()} style={navBtnStyle}>
          Actualizar
        </button>
      </div>

      {error ? <p style={{ color: '#b91c1c', marginBottom: 16 }}>{error}</p> : null}
      {loading ? <p style={{ color: tokens.colors.neutral[500] }}>Cargando…</p> : null}

      <section
        style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          padding: 16,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 4,
            marginBottom: 8,
          }}
        >
          {DAY_HEADERS.map((name) => (
            <div
              key={name}
              style={{
                textAlign: 'center',
                fontSize: 11,
                fontWeight: 600,
                color: tokens.colors.neutral[500],
                textTransform: 'uppercase',
              }}
            >
              {name}
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {weeks.flat().map((day) => {
            const key = calendarDayKey(day.date);
            const dayMeetings = meetingsByDay.get(key) ?? [];
            const count = dayMeetings.length;
            const cancelledCount = dayMeetings.filter(isCancelled).length;
            const isToday = sameDay(day.date, today);
            const isSelected = selectedDate ? sameDay(day.date, selectedDate) : false;
            return (
              <button
                key={day.date.toISOString()}
                type="button"
                onClick={() => {
                  setSelectedDate(new Date(day.date));
                }}
                style={{
                  minHeight: 72,
                  padding: 6,
                  borderRadius: 8,
                  border: isSelected ? '2px solid #6366f1' : '1px solid #e2e8f0',
                  background: isToday ? '#eef2ff' : day.isCurrentMonth ? '#fff' : '#f8fafc',
                  opacity: day.isCurrentMonth ? 1 : 0.45,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: isToday ? 700 : 500 }}>{day.date.getDate()}</div>
                {count > 0 ? (
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 11,
                      fontWeight: 600,
                      color: cancelledCount === count ? '#94a3b8' : '#4f46e5',
                    }}
                  >
                    {count} demo{count > 1 ? 's' : ''}
                    {cancelledCount > 0 ? (
                      <span style={{ color: '#94a3b8', fontWeight: 500 }}>
                        {' '}
                        · {cancelledCount} canc.
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>
      </section>

      <section
        style={{
          marginTop: 20,
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          padding: 16,
        }}
      >
        <h2 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700 }}>
          {selectedDate
            ? new Intl.DateTimeFormat('es-UY', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              }).format(selectedDate)
            : 'Seleccioná un día'}
        </h2>
        {selectedMeetings.length === 0 ? (
          <p style={{ margin: 0, color: tokens.colors.neutral[500], fontSize: 14 }}>
            Sin reuniones este día.
          </p>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {selectedMeetings.map((m) => {
              const eb = emailBadge(m);
              const wb = waBadge(m);
              const cancelled = isCancelled(m);
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(m.id)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      border: '1px solid #e2e8f0',
                      borderRadius: 10,
                      padding: 12,
                      background: cancelled ? '#f8fafc' : '#fff',
                      cursor: 'pointer',
                      opacity: cancelled ? 0.85 : 1,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 8,
                        alignItems: 'baseline',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 15,
                          textDecoration: cancelled ? 'line-through' : undefined,
                          color: cancelled ? tokens.colors.neutral[500] : undefined,
                        }}
                      >
                        {formatTimeUy(m.scheduledAt)}
                      </div>
                      {cancelled ? <span style={chipStyle('#64748b')}>Cancelada</span> : null}
                    </div>
                    <div style={{ fontSize: 14, marginTop: 4 }}>{m.name}</div>
                    <div style={{ fontSize: 13, color: tokens.colors.neutral[600] }}>{m.company}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                      {!cancelled ? (
                        <>
                          <span style={chipStyle(eb.color)}>{eb.label}</span>
                          <span style={chipStyle(wb.color)}>{wb.label}</span>
                        </>
                      ) : null}
                      {m.clientOutcome ? (
                        <span style={chipStyle('#4f46e5')}>
                          {OUTCOME_OPTIONS.find((o) => o.value === m.clientOutcome)?.label ??
                            m.clientOutcome}
                        </span>
                      ) : null}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {selected ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Detalle demo ${selected.name}`}
          style={modalBackdrop}
          onClick={() => setSelectedId(null)}
        >
          <div style={modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Detalle</h2>
              <button type="button" onClick={() => setSelectedId(null)} style={navBtnStyle}>
                Cerrar
              </button>
            </div>

            <div style={{ marginTop: 12, fontSize: 14, lineHeight: 1.5 }}>
              {selectedCancelled ? (
                <div style={{ marginBottom: 10 }}>
                  <span style={chipStyle('#64748b')}>Cancelada</span>
                </div>
              ) : null}
              <div style={{ fontWeight: 700, fontSize: 18 }}>{selected.name}</div>
              <div style={{ color: tokens.colors.neutral[600] }}>{selected.company}</div>
              <div style={{ marginTop: 8 }}>{selected.meetingLabel}</div>
              <a href={`mailto:${selected.email}`} style={{ color: '#4f46e5', fontSize: 13 }}>
                {selected.email}
              </a>
              {selected.phone ? (
                <div style={{ fontSize: 13, color: tokens.colors.neutral[600] }}>{selected.phone}</div>
              ) : null}
              {selected.meetUrl && !selectedCancelled ? (
                <a
                  href={selected.meetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    marginTop: 10,
                    padding: '8px 12px',
                    background: '#4f46e5',
                    color: '#fff',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  Unirse a Meet
                </a>
              ) : null}
              {selected.message ? (
                <p
                  style={{
                    marginTop: 12,
                    padding: 10,
                    background: '#f8fafc',
                    borderRadius: 8,
                    fontSize: 13,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {selected.message}
                </p>
              ) : null}
            </div>

            <label style={{ display: 'block', marginTop: 16, fontSize: 13, fontWeight: 600 }}>
              Resultado del cliente
              <select
                value={outcomeDraft}
                onChange={(e) => setOutcomeDraft(e.target.value)}
                style={{ ...inputStyle, marginTop: 6 }}
              >
                {OUTCOME_OPTIONS.map((o) => (
                  <option key={o.value || 'pending'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: 'block', marginTop: 12, fontSize: 13, fontWeight: 600 }}>
              Notas internas
              <textarea
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                rows={5}
                style={{ ...inputStyle, marginTop: 6, resize: 'vertical' }}
                placeholder="Notas del equipo…"
              />
            </label>

            <button
              type="button"
              disabled={saving}
              onClick={() => void saveDetail()}
              style={{ ...primaryBtnStyle, marginTop: 12, opacity: saving ? 0.7 : 1 }}
            >
              {saving ? 'Guardando…' : 'Guardar detalle'}
            </button>

            {!selectedCancelled ? (
              <div style={{ marginTop: 20, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Recordatorios</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  <span style={chipStyle(emailBadge(selected).color)}>{emailBadge(selected).label}</span>
                  <span style={chipStyle(waBadge(selected).color)}>{waBadge(selected).label}</span>
                </div>
                {selected.reminderWhatsappError ? (
                  <p style={{ margin: '0 0 8px', fontSize: 12, color: '#b91c1c' }}>
                    {selected.reminderWhatsappError}
                  </p>
                ) : null}
                {!waConfigured ? (
                  <p style={{ margin: '0 0 8px', fontSize: 12, color: '#a16207' }}>
                    WhatsApp: configurá DEMO_WHATSAPP_* (token, phone_number_id, template).
                  </p>
                ) : null}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button
                    type="button"
                    disabled={reminding}
                    onClick={() => void sendReminder(['email'], Boolean(selected.reminderEmailSentAt))}
                    style={navBtnStyle}
                  >
                    Enviar recordatorio email
                    {selected.reminderEmailSentAt ? ' (reenviar)' : ''}
                  </button>
                  <button
                    type="button"
                    disabled={reminding || !waConfigured || !selected.phone}
                    onClick={() =>
                      void sendReminder(['whatsapp'], Boolean(selected.reminderWhatsappSentAt))
                    }
                    style={navBtnStyle}
                    title={!selected.phone ? 'Sin teléfono en la solicitud' : undefined}
                  >
                    Enviar recordatorio WhatsApp
                    {selected.reminderWhatsappSentAt ? ' (reenviar)' : ''}
                  </button>
                  <button
                    type="button"
                    disabled={reminding}
                    onClick={() =>
                      void sendReminder(
                        waConfigured && selected.phone ? ['email', 'whatsapp'] : ['email'],
                        true
                      )
                    }
                    style={primaryBtnStyle}
                  >
                    {reminding ? 'Enviando…' : 'Enviar ambos ahora'}
                  </button>
                </div>
              </div>
            ) : null}

            {actionMsg ? (
              <p style={{ marginTop: 12, fontSize: 13, color: tokens.colors.neutral[700] }}>{actionMsg}</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function chipStyle(color: string): React.CSSProperties {
  return {
    fontSize: 11,
    fontWeight: 600,
    color,
    background: `${color}18`,
    borderRadius: 999,
    padding: '2px 8px',
  };
}

const navBtnStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 14,
};

const primaryBtnStyle: React.CSSProperties = {
  ...navBtnStyle,
  background: '#4f46e5',
  borderColor: '#4f46e5',
  color: '#fff',
  fontWeight: 600,
};

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  fontSize: 14,
  fontFamily: 'inherit',
};

const modalBackdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: 16,
};

const modalBox: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  padding: 24,
  maxWidth: 520,
  width: '100%',
  maxHeight: '90vh',
  overflowY: 'auto',
  border: '1px solid #e2e8f0',
  boxShadow: '0 20px 50px rgba(0,0,0,0.12)',
};
